/**
 * Shared behavior for Tuang Coffee custom sections (tng- and tuang- prefixed).
 * Replaces the reveal-on-scroll / parallax / tilt scripts that used to be
 * duplicated inline in every custom section.
 *
 * A section opts in by marking its root element `data-tng-root` and
 * configuring behavior via data-attributes (see README block below).
 * Shopify's Theme Editor doesn't re-execute inline <script> tags when a
 * section re-renders after a setting change, so lifecycle is driven by the
 * shopify:section:load / shopify:section:unload events instead, which this
 * file listens for once, globally, for the lifetime of the page.
 *
 * Config data-attributes (all optional, read off the [data-tng-root] element):
 *   data-tng-reveal-selector   CSS selector for elements to reveal (default "[data-r]")
 *   data-tng-reveal-class      class added when an element reveals (default "in")
 *   data-tng-reveal-mode       "observer" (default) or "stagger"
 *   data-tng-reveal-threshold  IntersectionObserver threshold (observer mode)
 *   data-tng-reveal-margin     IntersectionObserver rootMargin (observer mode)
 *   data-tng-reveal-base       stagger start delay in ms (stagger mode)
 *   data-tng-reveal-step       stagger delay increment in ms (stagger mode)
 *   data-tng-parallax-target   selector (within root) for a scroll-parallax background
 *   data-tng-parallax-scale    base CSS scale() applied to the parallax target
 *   data-tng-parallax-strength translateY multiplier in px for the parallax target
 *   data-tng-pause-target      selector (within root) for elements whose CSS
 *                              animation should pause while root is off-screen
 * Elements inside the root marked [data-tng-tilt] automatically get a pointer
 * 3D tilt effect; this needs no extra config.
 */
(function () {
  'use strict';

  if (window.TngReveal) return;

  var reduceMotionQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  function prefersReducedMotion() {
    return !!(reduceMotionQuery && reduceMotionQuery.matches);
  }

  var registry = new WeakMap();

  function num(root, name, fallback) {
    var v = root.getAttribute(name);
    return v === null ? fallback : parseFloat(v);
  }
  function str(root, name, fallback) {
    var v = root.getAttribute(name);
    return v === null ? fallback : v;
  }

  function initRoot(root) {
    if (registry.has(root)) return;

    var reduce = prefersReducedMotion();
    var cleanupFns = [];

    var selector = str(root, 'data-tng-reveal-selector', '[data-r]');
    if (selector) {
      var activeClass = str(root, 'data-tng-reveal-class', 'in');
      var mode = str(root, 'data-tng-reveal-mode', 'observer');
      var els = root.querySelectorAll(selector);

      var reveal = function (el) { el.classList.add(activeClass); };

      if (reduce) {
        els.forEach(reveal);
      } else if (mode === 'stagger') {
        var base = num(root, 'data-tng-reveal-base', 120);
        var step = num(root, 'data-tng-reveal-step', 140);
        els.forEach(function (el, i) {
          var t = setTimeout(function () { reveal(el); }, base + i * step);
          cleanupFns.push(function () { clearTimeout(t); });
        });
      } else if ('IntersectionObserver' in window) {
        var threshold = num(root, 'data-tng-reveal-threshold', 0.15);
        var rootMargin = str(root, 'data-tng-reveal-margin', '0px');
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              reveal(entry.target);
              io.unobserve(entry.target);
            }
          });
        }, { threshold: threshold, rootMargin: rootMargin });
        els.forEach(function (el) { io.observe(el); });
        cleanupFns.push(function () { io.disconnect(); });
      } else {
        els.forEach(reveal);
      }
    }

    var parallaxSelector = str(root, 'data-tng-parallax-target', null);
    if (!reduce && parallaxSelector) {
      var target = root.querySelector(parallaxSelector);
      if (target) {
        var scale = num(root, 'data-tng-parallax-scale', 1);
        var strength = num(root, 'data-tng-parallax-strength', 0);
        var ticking = false;
        var onScroll = function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            var r = root.getBoundingClientRect();
            var p = r.top / window.innerHeight;
            target.style.transform = 'scale(' + scale + ') translateY(' + (p * -strength) + 'px)';
            ticking = false;
          });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        cleanupFns.push(function () { window.removeEventListener('scroll', onScroll); });
      }
    }

    if (!reduce) {
      root.querySelectorAll('[data-tng-tilt]').forEach(function (card) {
        var onMove = function (ev) {
          var r = card.getBoundingClientRect();
          var px = (ev.clientX - r.left) / r.width - 0.5;
          var py = (ev.clientY - r.top) / r.height - 0.5;
          card.style.transform = 'perspective(800px) rotateY(' + (px * 6) + 'deg) rotateX(' + (-py * 6) + 'deg)';
        };
        var onLeave = function () { card.style.transform = ''; };
        card.addEventListener('pointermove', onMove);
        card.addEventListener('pointerleave', onLeave);
      });
    }

    var pauseSelector = str(root, 'data-tng-pause-target', null);
    if (pauseSelector && 'IntersectionObserver' in window) {
      var pauseTargets = root.querySelectorAll(pauseSelector);
      if (pauseTargets.length) {
        var pio = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            pauseTargets.forEach(function (t) {
              t.classList.toggle('tng-anim-paused', !entry.isIntersecting);
            });
          });
        }, { threshold: 0 });
        pio.observe(root);
        cleanupFns.push(function () { pio.disconnect(); });
      }
    }

    registry.set(root, function cleanup() {
      cleanupFns.forEach(function (fn) { fn(); });
      registry.delete(root);
    });
  }

  function destroyRoot(root) {
    var cleanup = registry.get(root);
    if (cleanup) cleanup();
  }

  function scan(node) {
    if (!node || !node.querySelectorAll) return;
    var roots = node.matches && node.matches('[data-tng-root]') ? [node] : [];
    roots = roots.concat(Array.prototype.slice.call(node.querySelectorAll('[data-tng-root]')));
    roots.forEach(initRoot);
  }

  function destroyIn(node) {
    if (!node || !node.querySelectorAll) return;
    if (node.matches && node.matches('[data-tng-root]')) destroyRoot(node);
    node.querySelectorAll('[data-tng-root]').forEach(destroyRoot);
  }

  document.addEventListener('shopify:section:load', function (event) { scan(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroyIn(event.target); });

  window.TngReveal = { initRoot: initRoot, destroyRoot: destroyRoot };

  scan(document);
})();
